import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import moment from "moment";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";

import DataTable from "examples/Tables/DataTable";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import CreateTransactionButton from "layouts/order-management-details/CreateTransactionButton";
import MpesaTransactionButton from "layouts/order-management-details/MpesaTransactionButton";
import CreateComplaintButton from "layouts/order-management-details/CreateComplaintButton";

// Images
import brandDark from "assets/images/somosure/logo.png";

import { getOrderSingle } from "actions/orderActions";

function OrderManagementDetails() {
  const dispatch = useDispatch();
  const { id } = useParams();

  const orderSingle = useSelector((state) => state.orderSingle);
  const { singleOrder } = orderSingle;

  useEffect(() => {
    dispatch(getOrderSingle(id));
  }, [dispatch, singleOrder?.length, id]);

  const getGrandTotal = () => {
    const addonsTotal = singleOrder?.addons?.reduce((acc, item) => {
      return acc + item.addonAmount;
    }, 0);
    return addonsTotal + singleOrder?.product?.amount;
  };

  function renderTransactionsRows(data) {
    const values = [];
    data?.forEach((item, index) => {
      const newElement = {
        code: (
          <MDTypography
            key={index}
            variant="caption"
            color="text"
            fontWeight="medium"
          >
            {item?.transactionCode}
          </MDTypography>
        ),
        method: (
          <MDBox maxWidth={300}>
            <MDTypography variant="caption" color="text" fontWeight="medium">
              {item?.transactionMethod}
            </MDTypography>
          </MDBox>
        ),
        amount: (
          <MDTypography variant="caption" color="text" fontWeight="medium">
            Ksh. {item?.amount}
          </MDTypography>
        ),
        date: (
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {moment(item?.updated_at).format("DD/MM/YYYY HH:mm:ss")}
          </MDTypography>
        ),
        status: (
          <MDBox width="8rem" textAlign="center">
            <MDBox ml={-1}>
              <MDBadge
                badgeContent={item?.status}
                color={
                  item?.status == "pending"
                    ? "info"
                    : item?.status == "processing"
                    ? "primary"
                    : item?.status == "completed"
                    ? "success"
                    : item?.status == "rejected"
                    ? "dark"
                    : "light"
                }
                variant="gradient"
                size="sm"
              />
            </MDBox>
          </MDBox>
        ),
      };
      values.push(newElement);
    });
    return values;
  }

  function renderAddonRows(data) {
    const values = [];
    data?.forEach((item, index) => {
      const newElement = {
        name: (
          <MDTypography
            key={index}
            variant="caption"
            color="text"
            fontWeight="medium"
          >
            {item?.addonName}
          </MDTypography>
        ),
        description: (
          <MDBox maxWidth={300}>
            <MDTypography variant="caption" color="text" fontWeight="medium">
              {item?.description}
            </MDTypography>
          </MDBox>
        ),
        addonAmount: (
          <MDTypography variant="caption" color="text" fontWeight="medium">
            Ksh. {item?.addonAmount}
          </MDTypography>
        ),
        active: (
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {item?.active ? (
              <MDBox ml={-1}>
                <MDBadge
                  badgeContent="active"
                  color="dark"
                  variant="gradient"
                  size="sm"
                />
              </MDBox>
            ) : (
              <MDBox ml={-1}>
                <MDBadge
                  badgeContent="inactive"
                  color="success"
                  variant="gradient"
                  size="sm"
                />
              </MDBox>
            )}
          </MDTypography>
        ),
        // action: (
        //   <>
        //     <MDTypography
        //       component="a"
        //       href="#"
        //       variant="caption"
        //       color="text"
        //       fontWeight="medium"
        //     >
        //       Edit
        //     </MDTypography>
        //   </>
        // ),
      };
      values.push(newElement);
    });
    return values;
  }

  function renderRequirementsRows(data) {
    const values = [];
    data?.forEach((item, index) => {
      const newElement = {
        response: (
          <MDBox maxWidth={150}>
            <MDTypography
              key={index}
              variant="caption"
              color="text"
              fontWeight="medium"
            >
              {item?.responseRequirement}
            </MDTypography>
          </MDBox>
        ),
        type: (
          <MDBox maxWidth={200}>
            <MDTypography variant="caption" color="text" fontWeight="medium">
              {item?.responseRequirementType}
            </MDTypography>
          </MDBox>
        ),
        date: (
          <MDTypography variant="caption" color="text" fontWeight="medium">
            {moment(item?.updated_at).format("DD/MM/YYYY")}
          </MDTypography>
        ),
      };
      values.push(newElement);
    });
    return values;
  }

  return (
    <DashboardLayout>
      <DashboardNavbar
        title={`${singleOrder?.requestedBy?.name}'s Order Details`}
      />
      <MDBox mt={6} mb={6}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} lg={8}>
            <Card>
              <MDBox p={5} lineHeight={0}>
                <MDBox display="flex" justifyContent="space-between">
                  <MDBox display="flex" alignItems="center">
                    {/* {brandDark && ( */}
                    <MDBox
                      component="img"
                      src={brandDark}
                      alt="Brand"
                      width="9rem"
                    />
                  </MDBox>

                  <MDBox display="flex" alignItems="center">
                    <MDBox
                      component="img"
                      src={singleOrder?.supplier?.supplierLogo}
                      alt="Brand"
                      width="5rem"
                    />
                  </MDBox>
                </MDBox>

                <MDBox display="flex" justifyContent="space-between" mb={10}>
                  <MDBox>
                    <MDTypography variant="h6">
                      Vertical Lakes Fisheries
                    </MDTypography>
                    <MDTypography variant="h6">Nairobi, Kenya</MDTypography>
                    <MDTypography variant="h6">
                      Email:{" "}
                      <MDTypography
                        variant="button"
                        color="text"
                        fontWeight="regular"
                      >
                        verticallakes@mail.com
                      </MDTypography>
                    </MDTypography>
                    <MDTypography variant="h6">
                      Phone:{" "}
                      <MDTypography
                        variant="button"
                        color="text"
                        fontWeight="regular"
                      >
                        +254 700 112344
                      </MDTypography>
                    </MDTypography>
                  </MDBox>

                  <MDBox textAlign="right">
                    <MDTypography variant="h6">
                      {singleOrder?.supplier?.supplierName}
                    </MDTypography>
                    <MDTypography variant="h6">
                      {singleOrder?.supplier?.supplierAddress}
                    </MDTypography>
                    <MDTypography variant="h6">
                      Email:{" "}
                      <MDTypography
                        variant="button"
                        color="text"
                        fontWeight="regular"
                      >
                        {singleOrder?.supplier?.supplierEmail}
                      </MDTypography>
                    </MDTypography>
                    <MDTypography variant="h6">
                      Phone:{" "}
                      <MDTypography
                        variant="button"
                        color="text"
                        fontWeight="regular"
                      >
                        {singleOrder?.supplier?.supplierPhone}
                      </MDTypography>
                    </MDTypography>
                  </MDBox>
                </MDBox>

                <MDBox display="flex" justifyContent="space-between" mb={5}>
                  <MDBox>
                    <MDTypography variant="h6">Order No.</MDTypography>
                    <MDTypography
                      variant="button"
                      color="text"
                      fontWeight="regular"
                    >
                      #{singleOrder?._id}
                    </MDTypography>
                  </MDBox>

                  <MDBox textAlign="right">
                    <MDTypography variant="h6">
                      Order Created:{" "}
                      <MDTypography
                        variant="button"
                        color="text"
                        fontWeight="regular"
                      >
                        {moment(singleOrder?.created_at).format("DD/MM/YYYY")}
                      </MDTypography>
                    </MDTypography>
                    <MDTypography variant="h6">
                      Service:{" "}
                      <MDTypography
                        variant="button"
                        color="text"
                        fontWeight="regular"
                      >
                        {singleOrder?.service?.mainService} -{" "}
                        {singleOrder?.service?.service}
                      </MDTypography>
                    </MDTypography>
                  </MDBox>
                </MDBox>

                <MDBox p={3} mb={5}>
                  <DataTable
                    table={{
                      columns: [
                        {
                          Header: "Client",
                          accessor: "client",
                          width: "30%",
                          align: "left",
                        },
                        {
                          Header: "Product Number",
                          accessor: "product",
                          width: "30%",
                          align: "left",
                        },

                        {
                          Header: "Amount",
                          accessor: "amount",
                          width: "20%",
                          align: "center",
                        },
                        {
                          Header: "Order State",
                          accessor: "state",
                          width: "10%",
                          align: "center",
                        },
                        {
                          Header: "period",
                          accessor: "period",
                          width: "10%",
                          align: "center",
                        },
                      ],

                      rows: [
                        {
                          client: (
                            <MDBox maxWidth={300}>
                              <MDTypography
                                variant="caption"
                                color="text"
                                fontWeight="medium"
                              >
                                {singleOrder?.requestedBy?.name}
                              </MDTypography>
                            </MDBox>
                          ),
                          product: (
                            <MDBox>
                              <MDTypography
                                variant="caption"
                                color="text"
                                fontWeight="medium"
                                mb={1}
                              >
                                {singleOrder?.product?.productName} -{" "}
                                {singleOrder?.product?.productNumber}
                              </MDTypography>
                            </MDBox>
                          ),
                          amount: (
                            <MDTypography
                              variant="caption"
                              color="text"
                              fontWeight="medium"
                            >
                              Ksh. {singleOrder?.product?.amount}
                            </MDTypography>
                          ),
                          state: (
                            <MDBox width="8rem" textAlign="center">
                              <MDBox ml={-1}>
                                <MDBadge
                                  badgeContent={singleOrder?.status}
                                  color={
                                    singleOrder?.status == "pending"
                                      ? "info"
                                      : singleOrder?.status == "processing"
                                      ? "primary"
                                      : singleOrder?.status == "approved"
                                      ? "success"
                                      : singleOrder?.status == "rejected"
                                      ? "dark"
                                      : "light"
                                  }
                                  variant="gradient"
                                  size="sm"
                                />
                              </MDBox>
                            </MDBox>
                          ),
                          period: (
                            <>
                              <MDTypography
                                component="a"
                                href="#"
                                variant="caption"
                                color="text"
                                fontWeight="medium"
                              >
                                {moment(singleOrder?.startDate).format(
                                  "DD/MM/YYYY"
                                )}{" "}
                                to{" "}
                                {moment(singleOrder?.endDate).format(
                                  "DD/MM/YYYY"
                                )}
                              </MDTypography>
                            </>
                          ),
                        },
                      ],
                    }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                </MDBox>

                <MDBox>
                  <MDTypography variant="h6">Chosen Addons</MDTypography>
                </MDBox>
                <MDBox p={3} mb={5}>
                  <DataTable
                    table={{
                      columns: [
                        {
                          Header: "Addon Name",
                          accessor: "name",
                          width: "30%",
                          align: "left",
                        },
                        {
                          Header: "Description",
                          accessor: "description",
                          width: "30%",
                          align: "left",
                        },
                        {
                          Header: "Amount",
                          accessor: "addonAmount",
                          width: "20%",
                          align: "center",
                        },
                        {
                          Header: "Active",
                          accessor: "active",
                          width: "10%",
                          align: "center",
                        },
                        // {
                        //   Header: "action",
                        //   accessor: "action",
                        //   width: "10%",
                        //   align: "center",
                        // },
                      ],

                      rows: renderAddonRows(singleOrder?.addons),
                    }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                </MDBox>

                <MDBox>
                  <MDTypography variant="h6">
                    Provided Product Requirements
                  </MDTypography>
                </MDBox>
                <MDBox p={3} mb={5}>
                  <DataTable
                    table={{
                      columns: [
                        {
                          Header: "Response Required",
                          accessor: "response",
                          width: "20%",
                          align: "left",
                        },
                        {
                          Header: "Response Type",
                          accessor: "type",
                          width: "30%",
                          align: "left",
                        },
                        {
                          Header: "Provided",
                          accessor: "date",
                          width: "10%",
                          align: "center",
                        },
                      ],

                      rows: renderRequirementsRows(
                        singleOrder?.responseRequirements
                      ),
                    }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                </MDBox>

                <MDBox>
                  <MDTypography variant="h6">Transactions List</MDTypography>
                </MDBox>
                <MDBox p={3} mb={5}>
                  <DataTable
                    table={{
                      columns: [
                        {
                          Header: "Transaction Code",
                          accessor: "code",
                          width: "30%",
                          align: "left",
                        },
                        {
                          Header: "Transaction Method",
                          accessor: "method",
                          width: "30%",
                          align: "left",
                        },
                        {
                          Header: " Paid Amount",
                          accessor: "amount",
                          width: "20%",
                          align: "center",
                        },
                        {
                          Header: "Transaction Date",
                          accessor: "date",
                          width: "10%",
                          align: "center",
                        },
                        {
                          Header: "Transaction Status",
                          accessor: "status",
                          width: "10%",
                          align: "center",
                        },
                      ],

                      rows: renderTransactionsRows(singleOrder?.transactions),
                    }}
                    isSorted={false}
                    entriesPerPage={false}
                    showTotalEntries={false}
                    noEndBorder
                  />
                </MDBox>

                <MDBox>
                  <MDTypography variant="h6">Make Payment</MDTypography>
                </MDBox>

                <MDBox display="flex" justifyContent="space-between" mb={5}>
                  <MDBox>
                    <MDBox>
                      <MDTypography variant="body2" py={2}>
                        Initiate an MPESA transaction:{" "}
                      </MDTypography>
                      <MpesaTransactionButton selectOrder={singleOrder} />
                    </MDBox>
                  </MDBox>

                  <MDBox>
                    <MDBox>
                      <MDTypography variant="body2" py={2}>
                        Register a manual transaction:{" "}
                      </MDTypography>
                      <CreateTransactionButton selectOrder={singleOrder} />
                    </MDBox>
                  </MDBox>

                  <MDBox textAlign="flex-end" mr={5}>
                    <MDBox
                      display="flex"
                      flexDirection="row"
                      py={1}
                      justifyContent="flex-end"
                    >
                      <MDTypography variant="body2" color="text">
                        Product Order Total:{" "}
                      </MDTypography>
                      <MDTypography variant="h6" pl={3}>
                        Ksh. {singleOrder?.product?.amount}
                      </MDTypography>
                    </MDBox>
                    <MDBox
                      display="flex"
                      flexDirection="row"
                      py={1}
                      justifyContent="flex-end"
                    >
                      <MDTypography variant="body2" color="text">
                        Total + Addons:{" "}
                      </MDTypography>
                      <MDTypography variant="h5" pl={3}>
                        Ksh. {getGrandTotal()}
                      </MDTypography>
                    </MDBox>
                    <MDBox
                      display="flex"
                      flexDirection="row"
                      py={1}
                      justifyContent="flex-end"
                    >
                      <MDTypography variant="body2" color="text">
                        Amount Paid:{" "}
                      </MDTypography>
                      <MDTypography variant="h5" pl={3}>
                        Ksh. {singleOrder?.totalAmountPaid}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </MDBox>

                <MDBox>
                  <MDTypography variant="h6">Make Complaint</MDTypography>
                </MDBox>

                <MDBox display="flex" justifyContent="space-between" mb={5}>
                  <MDBox>
                    <MDTypography variant="body2" py={2}>
                      Make a complaint with this order:{" "}
                    </MDTypography>
                    <CreateComplaintButton selectOrder={singleOrder} />
                  </MDBox>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default OrderManagementDetails;
